import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { JobCard } from '../../components/Cards';
import {
  Button, Chip, Empty, Input, Loading,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useApp } from '../../context/AppContext';
import CityPicker from '../../components/CityPicker';
import { EXPERIENCE_FILTERS, JOB_TYPES, SALARY_FILTERS } from '../../constants';

const JobsPage = () => {
  const navigate = useNavigate();
  const { location } = useApp();

  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState('');
  const [jobType, setJobType] = useState('');
  // Where to look and how much experience the person has.
  const [place, setPlace] = useState({ city: '', district: '', state: '' });
  const [experience, setExperience] = useState('');
  // Trade and pay - the two things people filter by after location.
  const [category, setCategory] = useState('');
  const [minSalary, setMinSalary] = useState('');
  const [cats, setCats] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.jobs.list({
        search: search || undefined,
        jobType: jobType || undefined,
        city: place.city || undefined,
        experience: experience === '' ? undefined : experience,
        category: category || undefined,
        minSalary: minSalary || undefined,
        limit: 30,
      });
      setJobs(res.data || []);
    } catch (e) {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [search, jobType, place.city, experience, category, minSalary]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  useEffect(() => {
    api.categories.list({ limit: 60 }).then((r) => setCats(r.data || [])).catch(() => setCats([]));
    api.jobs.saved().then((r) => setSavedIds((r.data || []).map((j) => j._id))).catch(() => setSavedIds([]));
  }, []);

  const toggleSave = async (job) => {
    // Flip it straight away; put it back if the server disagrees.
    const was = savedIds.includes(job._id);
    setSavedIds((ids) => (was ? ids.filter((id) => id !== job._id) : [...ids, job._id]));
    try {
      await api.jobs.toggleSaved(job._id);
    } catch (e) {
      setSavedIds((ids) => (was ? [...ids, job._id] : ids.filter((id) => id !== job._id)));
    }
  };

  return (
    <div>
      <PageHeader
        title="Jobs"
        subtitle={`${jobs.length} openings ${place.city ? `in ${place.city}` : `near ${location.city}`}`}
        right={(
          <div className="row" style={{ gap: 8 }}>
            <Button title="Saved jobs" variant="light" size="sm" onClick={() => navigate('/saved-jobs')} />
            <Button title="My applications" variant="light" size="sm" onClick={() => navigate('/my-applications')} />
          </div>
        )}
      />

      <div className="card sh-sm" style={{ marginBottom: 16 }}>
        <Input
          icon="🔍"
          placeholder="Search job title, company or skill"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div className="split-2" style={{ gap: 12, marginBottom: 12 }}>
          <CityPicker
            label="Job location"
            value={place}
            onChange={({ city, district, state }) => setPlace({ city, district, state })}
          />
          <div>
            <label className="field-label">Your experience</label>
            <div className="row wrap" style={{ gap: 8 }}>
              {EXPERIENCE_FILTERS.map((e) => (
                <Chip key={e.value || 'any'} label={e.label} active={experience === e.value} onClick={() => setExperience(e.value)} />
              ))}
            </div>
            <div className="t-tiny" style={{ marginTop: 6 }}>We show jobs asking for that much experience or less.</div>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label className="field-label">Salary</label>
          <div className="row wrap" style={{ gap: 8 }}>
            {SALARY_FILTERS.map((s) => (
              <Chip key={s.value || 'any'} label={s.label} active={minSalary === s.value} onClick={() => setMinSalary(s.value)} />
            ))}
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label className="field-label">Work category</label>
          <div className="row wrap" style={{ gap: 8 }}>
            <Chip label="All work" active={!category} onClick={() => setCategory('')} />
            {cats.map((c) => (
              <Chip key={c._id} label={`${c.icon || ''} ${c.name}`.trim()} active={category === c._id} onClick={() => setCategory(c._id)} />
            ))}
          </div>
        </div>

        {place.city || experience !== '' || category || minSalary ? (
          <button type="button" className="link-btn" style={{ margin: '10px 0' }}
            onClick={() => {
              setPlace({ city: '', district: '', state: '' }); setExperience(''); setCategory(''); setMinSalary('');
            }}>
            Clear filters
          </button>
        ) : null}

        <div className="row no-bar" style={{ gap: 8, overflowX: 'auto' }}>
          {[{ value: '', label: 'All types' }, ...JOB_TYPES].map((t) => (
            <Chip
              key={t.value || 'all'}
              label={t.label}
              active={jobType === t.value}
              onClick={() => setJobType(t.value)}
            />
          ))}
        </div>
      </div>

      {loading ? <Loading /> : !jobs.length ? (
        <Empty
          emoji="💼"
          title="No jobs found"
          text="Try a different search or check back soon — new jobs are posted daily."
        />
      ) : (
        <div className="grid grid-cards">
          {jobs.map((job, i) => (
            <Reveal key={job._id} delay={stagger(i, 45)}>
              <JobCard
                job={job}
                saved={savedIds.includes(job._id)}
                onToggleSave={() => toggleSave(job)}
                onClick={() => navigate(`/jobs/${job._id}`)}
              />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobsPage;
